// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { CROP_STATUS, getCurrencySymbol, isGerminationStep, isPlantStep } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { ExpandoCard, TitleCard } from '../components/common/card';
import { Confirmation } from '../components/common/dailog';
import { Options } from '../components/common/dropdown';
import EmptyContainer from '../components/common/empty/EmptyContainer';
import { PageError } from '../components/common/error';
import { PageLoading } from '../components/common/loading';
import AddCropDialog from '../components/crop/AddCropDialog';
import AddDefaultCropsDialog from '../components/crop/AddDefaultCropsDialog';
import CropTable from '../components/crop/CropTable';
import UploadCropsDialog from '../components/crop/UploadCropsDialog';
import { makeCropsCSV } from '../components/crop/makeCropsCSV';
import { sortCropsByName } from '../components/crop/sortCropsByName';
import { useGating, useUserContext } from '../context/UserContext';
import { Box, MenuItem } from '../lib/base';
import { AddIcon, DownloadIcon, ImportExportIcon, MergeIcon, PlaylistAddIcon } from '../lib/icons';
import { ARCHIVED_CROPS_QUERY, CROPS_EXPORT_QUERY, CROPS_QUERY } from '../queries/CropsQueries';
import type {
  ArchivedCropsHomeQuery,
  ArchivedCropsHomeQueryVariables,
  CombinePlantAndGermHomeMutation,
  CombinePlantAndGermHomeMutationVariables,
  CropsExportQuery,
  CropsExportQueryVariables,
  CropsHomeQuery,
  CropsHomeQueryVariables,
} from '../types/__generated__/graphql';
import { downloadCSV } from '../utils/createCSV';
import { evictCacheData } from '../utils/evictCacheData';

const COMBINE_PLANT_AND_GERM_MUTATION = gql`
  mutation CombinePlantAndGermHome {
    combinePlantAndGerm(input: { crops_to_update: [] }) {
      updated_crops {
        id
        updated_at
        steps {
          edges {
            node {
              id
              name
              time_to_start_after_first_step
            }
          }
        }
      }
      updated_tasks {
        id
        task_name
        updated_at
      }
      updated_crop_steps {
        id
        name
        updated_at
      }
      deleted_tasks {
        id
      }
      deleted_crop_steps {
        id
      }
      crops_unable_to_combine {
        crop_name
      }
    }
  }
`;

interface CropData {
  readonly id: string;
  readonly crop_name: string;
  readonly steps: {
    edges: Array<{
      node: {
        id: string;
        name: string;
        time_to_start_after_first_step: number;
      };
    }>;
  };
}
function checkGermMergePotential(crops: readonly CropData[]) {
  const cropsToUpdate: CropData[] = [];
  for (const crop of crops) {
    const steps = crop.steps.edges.map(({ node }) => node);
    const plantingSteps = steps.filter(({ name }) => isPlantStep(name));
    if (plantingSteps.length !== 1) {
      continue;
    }
    const plantStep = plantingSteps[0];
    const germStepsAtSameTime = steps.filter(
      ({ name, time_to_start_after_first_step, id }) =>
        id !== plantStep.id &&
        isGerminationStep(name) &&
        time_to_start_after_first_step === plantStep.time_to_start_after_first_step,
    );
    if (germStepsAtSameTime.length !== 1) {
      continue;
    }
    cropsToUpdate.push(crop);
  }

  return sortCropsByName(cropsToUpdate);
}

export default React.memo(function Crops() {
  const { canUseBasic } = useGating();
  const { data, error, loading } = useQuery<CropsHomeQuery, CropsHomeQueryVariables>(CROPS_QUERY);
  const [getArchivedCrops, { data: archivedData, error: archivedError, loading: archivedLoading }] = useLazyQuery<
    ArchivedCropsHomeQuery,
    ArchivedCropsHomeQueryVariables
  >(ARCHIVED_CROPS_QUERY);
  const [combinePlantAndGerm, { client: apolloClient, loading: combinePlantAndGermLoading }] = useMutation<
    CombinePlantAndGermHomeMutation,
    CombinePlantAndGermHomeMutationVariables
  >(COMBINE_PLANT_AND_GERM_MUTATION);
  const [getAllCrops, { loading: allCropsDataLoading }] = useLazyQuery<CropsExportQuery, CropsExportQueryVariables>(
    CROPS_EXPORT_QUERY,
    {
      fetchPolicy: 'network-only',
    },
  );
  const { permissions, preferredDateFormat, currencyCode } = useUserContext();
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [newCropOpen, setNewCropOpen] = React.useState(false);
  const [defaultsOpen, setDefaultsOpen] = React.useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [mergeGermStepsDialogOpen, setMergeGermStepsDialogOpen] = React.useState(false);
  const existingCropSet = React.useMemo(() => {
    if (data == null || data.crops == null) {
      return new Set<string>();
    }
    return new Set<string>(
      data.crops.edges.filter(({ node }) => node.status !== CROP_STATUS.ARCHIVED).map(({ node }) => node.crop_name),
    );
  }, [data]);

  const cropsToMerge = React.useMemo(() => {
    const crops = data?.crops?.edges?.map(({ node }) => node) ?? [];

    return checkGermMergePotential(crops);
  }, [data]);
  const hasCropsToMerge = React.useMemo(() => cropsToMerge.length > 0, [cropsToMerge]);

  const onSubmitMerge = React.useCallback(() => {
    void combinePlantAndGerm({
      onCompleted: (dataIn) => {
        if (dataIn?.combinePlantAndGerm == null) {
          enqueueSnackbar('Something went wrong!', { variant: 'error', autoHideDuration: 10000 });
        } else {
          const cropsUpdated = dataIn.combinePlantAndGerm.updated_crops.length;
          evictCacheData({
            apolloClient,
            taskIds: dataIn.combinePlantAndGerm.deleted_tasks.map((c) => c.id),
            cropStepIds: dataIn.combinePlantAndGerm.deleted_crop_steps.map((c) => c.id),
          });
          enqueueSnackbar(`Updated ${cropsUpdated} crops`, { variant: 'success' });
          setMergeGermStepsDialogOpen(false);
        }
      },
      update: (cache, { data }) => {
        const tasksUpdated = (data?.combinePlantAndGerm?.updated_tasks?.length ?? 0) > 0;
        const tasksDeleted = (data?.combinePlantAndGerm?.deleted_tasks?.length ?? 0) > 0;
        cache.modify({
          fields: {
            taskGroup(existingGroup, { DELETE }) {
              return tasksUpdated || tasksDeleted ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksUpdated || tasksDeleted ? DELETE : existingGroup;
            },
          },
        });
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  }, [combinePlantAndGerm, apolloClient]);
  const onClickExportCrops = React.useCallback(() => {
    void getAllCrops({
      onCompleted: (dataIn) => {
        const crops = dataIn.crops.edges
          .map(({ node }) => node)
          .filter((crop) => crop.status !== CROP_STATUS.ARCHIVED)
          .map((crop) => ({
            ...crop,
            steps: crop.steps.edges.map(({ node }) => node),
            sizes: crop.edge_sizes.edges.map(({ node }) => node),
          }));
        downloadCSV({
          content: makeCropsCSV(crops, getCurrencySymbol(currencyCode)),
          title: `Microgreen Manager Crops ${dayjs().format(preferredDateFormat.replaceAll('/', '-'))}`,
        });
        setOptionsClose(true);
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  }, [getAllCrops, preferredDateFormat, currencyCode]);

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const userCanEditCrops = permissions.can_edit_crops_blends;

  const content = React.useMemo(() => {
    if (loading) {
      return <PageLoading />;
    }
    if (error || data == null || data.crops == null) {
      return <PageError error={error} />;
    }

    const crops = data.crops.edges.map(({ node }) => node);
    const notArchived = crops.filter((crop) => crop.status !== CROP_STATUS.ARCHIVED);
    if (crops.length === 0) {
      return (
        <EmptyContainer
          title="You have no crops yet!"
          description={'Click the "Options" button to start adding your crops.'}
        />
      );
    }

    return <CropTable userCanEditCrops={userCanEditCrops} crops={notArchived} />;
  }, [loading, error, data, userCanEditCrops]);

  const archivedCropsContent = React.useMemo(() => {
    if (archivedError) {
      return <PageError error={archivedError} />;
    }
    if (archivedLoading) {
      return <PageLoading />;
    }
    if (archivedData?.crops == null) {
      return <PageError />;
    }

    const crops = archivedData.crops.edges
      .map(({ node }) => node)
      .filter((crop) => crop.status === CROP_STATUS.ARCHIVED);

    return <CropTable userCanEditCrops={userCanEditCrops} crops={crops} />;
  }, [archivedLoading, archivedError, archivedData, userCanEditCrops]);

  const handleNewCropClick = React.useCallback(() => setNewCropOpen(true), []);
  const handleDefaultsClick = React.useCallback(() => {
    setDefaultsOpen(true);
    setOptionsClose(true);
  }, []);
  const handleUploadClick = React.useCallback(() => setUploadDialogOpen(true), []);

  const handleDialogClose = React.useCallback(() => {
    setNewCropOpen(false);
    setDefaultsOpen(false);
    setUploadDialogOpen(false);
    setOptionsClose(true);
  }, []);

  const handleShowArchivedCrops = React.useCallback(() => {
    void getArchivedCrops();
  }, [getArchivedCrops]);

  const defaults = React.useMemo(
    () => (
      <MenuItem key="add-default-crops" onClick={handleDefaultsClick} disableRipple disabled={!canUseBasic}>
        <PlaylistAddIcon />
        Add Default Crops
      </MenuItem>
    ),
    [handleDefaultsClick, canUseBasic],
  );

  const newCrop = React.useMemo(
    () => (
      <MenuItem key="new-crop" disableRipple disabled={!canUseBasic} onClick={handleNewCropClick}>
        <AddIcon />
        New Crop
      </MenuItem>
    ),
    [handleNewCropClick, canUseBasic],
  );

  const uploadCrops = React.useMemo(
    () => (
      <MenuItem key="upload-crops" disableRipple disabled={!canUseBasic} onClick={handleUploadClick}>
        <ImportExportIcon />
        Import Crops With CSV
      </MenuItem>
    ),
    [handleUploadClick, canUseBasic],
  );
  const combinePlantAndGermItem = React.useMemo(
    () => (
      <MenuItem
        key="combine-plant-and-germ"
        disableRipple
        disabled={combinePlantAndGermLoading || !canUseBasic}
        onClick={() => setMergeGermStepsDialogOpen(true)}
      >
        <MergeIcon />
        Merge Plant and Germ Steps
      </MenuItem>
    ),
    [combinePlantAndGermLoading, canUseBasic],
  );
  const exportCropsToCsv = React.useMemo(
    () => (
      <MenuItem key="export-crops" disableRipple disabled={allCropsDataLoading} onClick={onClickExportCrops}>
        <DownloadIcon />
        Export Crops to CSV
      </MenuItem>
    ),
    [allCropsDataLoading, onClickExportCrops],
  );

  const optionItems = React.useMemo(() => {
    const out = [newCrop, defaults, uploadCrops];
    if (hasCropsToMerge) {
      out.push(combinePlantAndGermItem);
    }
    out.push(exportCropsToCsv);

    return out;
  }, [newCrop, defaults, uploadCrops, hasCropsToMerge, combinePlantAndGermItem, exportCropsToCsv]);
  const options = React.useMemo(
    () => <Options close={optionsClose} options={optionItems} />,
    [optionsClose, optionItems],
  );

  return (
    <>
      <Helmet>
        <title>Crops</title>
      </Helmet>
      <TitleCard title="Crops" rightElement={userCanEditCrops ? options : <></>} content={content} />
      <Box sx={{ mt: 2 }}>
        <ExpandoCard
          title="Archived Crops"
          rightElement={<div />}
          content={archivedCropsContent}
          onShowContent={handleShowArchivedCrops}
        />
      </Box>
      {newCropOpen && <AddCropDialog isOpen={newCropOpen} onClose={handleDialogClose} onSuccess={handleDialogClose} />}
      {defaultsOpen && (
        <AddDefaultCropsDialog
          isOpen={defaultsOpen}
          onClose={handleDialogClose}
          onSuccess={handleDialogClose}
          existingCropNames={existingCropSet}
        />
      )}
      {uploadDialogOpen && (
        <UploadCropsDialog isOpen={uploadDialogOpen} onClose={handleDialogClose} onSuccess={handleDialogClose} />
      )}
      {mergeGermStepsDialogOpen && (
        <Confirmation
          onClose={() => setMergeGermStepsDialogOpen(false)}
          onSubmit={onSubmitMerge}
          open={mergeGermStepsDialogOpen}
          disableSubmit={combinePlantAndGermLoading}
          title="Merge Plant and Germination Steps"
          description={`You have ${cropsToMerge.length} crop${cropsToMerge.length > 1 ? 's' : ''} with a plant and germination step on the same day. Submitting this action will automatically combine the plant and germination steps for all your crops going forward. Planned plantings will also have their tasks merged as well.`}
        />
      )}
    </>
  );
});
